import React, { useState, useEffect, useRef, ReactNode } from "react";
import { useTheme } from "@/components/ThemeProvider";
import { useTranslation } from "@/context/LanguageContext";
import logoDark from "@/assets/newlogo-dark.png";
import logoLight from "@/assets/newlogo-light.png";
import { PricingDialog } from "./PricingDialog";
import { InviteEarnDialog } from "@/components/InviteEarnDialog";
import { Crown, Gift, Share2, Pencil, Check, X, BookDown } from "lucide-react";
import { Room } from "@/types/rooms";
import { ContentItem } from "@/types/content";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface DashboardHeaderProps {
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
  room?: Room | null;
  onShareRoom?: () => void;
  chat?: ContentItem | null;
  onShareChat?: () => void;
  onContentTitleChange?: (newTitle: string) => void;
  showDownloadButton?: boolean;
  onDownload?: () => void;
  headerExtra?: ReactNode;
  headerExtraMobile?: ReactNode;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onToggleSidebar,
  isSidebarOpen,
  room,
  onShareRoom,
  chat,
  onShareChat,
  onContentTitleChange,
  showDownloadButton,
  onDownload,
  headerExtra,
  headerExtraMobile,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const logo = theme === "light" ? logoLight : logoDark;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [isInviteEarnOpen, setIsInviteEarnOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>("free");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(chat?.title || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const fetchSubscription = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      
      if (data) {
        setSubscriptionStatus(data.subscription_status);
      }
    };
    
    fetchSubscription();
  }, [user]);

  useEffect(() => {
    setEditedTitle(chat?.title || "");
  }, [chat?.title]);

  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  const isFreePlan = subscriptionStatus === "free" || !subscriptionStatus;

  // Determine if we should show the Share button (for either room or chat)
  const showShareButton = (room && onShareRoom) || (chat && onShareChat);
  const handleShare = room && onShareRoom ? onShareRoom : onShareChat;

  const handleStartEdit = () => {
    setEditedTitle(chat?.title || "");
    setIsEditingTitle(true);
  };

  const handleCancelEdit = () => {
    setEditedTitle(chat?.title || "");
    setIsEditingTitle(false);
  };

  const handleSaveTitle = async () => {
    if (!chat || !editedTitle.trim()) {
      handleCancelEdit();
      return;
    }

    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle === chat.title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      const { error } = await supabase
        .from("content")
        .update({ title: trimmedTitle })
        .eq("id", chat.id);

      if (error) throw error;

      onContentTitleChange?.(trimmedTitle);
      toast.success("Title updated");
      setIsEditingTitle(false);
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error("Failed to update title");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <header className={`sticky top-0 z-40 bg-background/80 backdrop-blur transition-all ${
      isScrolled ? "border-b border-border" : ""
    }`}>
      <div className="mx-auto px-2 lg:px-4">
        <div className="hidden py-2 lg:flex lg:flex-col">
          <div className="flex items-center justify-between">
            <div className="flex flex-1 items-center gap-2">
              {!isSidebarOpen && (
                <div className="my-auto flex items-center justify-center">
                  <button
                    type="button"
                    onClick={onToggleSidebar}
                    className="flex h-8 w-8 items-center justify-center rounded-xl hover:bg-muted transition"
                    aria-label="Toggle sidebar"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 6h16" />
                      <path d="M4 12h16" />
                      <path d="M4 18h16" />
                    </svg>
                  </button>
                  <a href="/dashboard">
                    <img src={logo} className="ml-2 h-10 w-auto cursor-pointer" alt="Shattara AI" />
                  </a>
                </div>
              )}
              
              {/* Breadcrumb: Room Name / Content Title */}
              {chat && (
                <div className="flex items-center gap-2">
                  {room ? (
                    <>
                      <span className="text-muted-foreground">/</span>
                      <a
                        href={`/room/${room.id}`}
                        className="text-base font-normal text-muted-foreground hover:text-primary transition-colors truncate max-w-[200px]"
                      >
                        {room.name}
                      </a>
                      <span className="text-muted-foreground">/</span>
                    </>
                  ) : null}
                  {isEditingTitle ? (
                    <div className="flex items-center">
                      <input
                        ref={inputRef}
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleSaveTitle}
                        className="h-8 w-48 text-sm bg-transparent px-1 py-0.5 focus:outline-none"
                        placeholder="Enter title..."
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-2 text-base font-normal text-primary hover:bg-transparent tracking-tight lg:w-[500px] xl:w-[600px]">
                      <button
                        onClick={handleStartEdit}
                        className="min-w-0 cursor-text truncate rounded-sm px-1 py-0.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 text-left"
                        aria-label="Click to edit title"
                      >
                        {chat.title}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center text-sm font-medium gap-2">
              {isFreePlan && (
                <button 
                  onClick={() => setIsPricingDialogOpen(true)}
                  className="inline-flex h-10 items-center justify-center rounded-full border-2 border-success/40 bg-success/5 px-3 py-2 text-center text-success hover:bg-success/10 transition-colors whitespace-nowrap"
                >
                  {t("common.upgrade")}
                </button>
              )}
              {headerExtra ? (
                headerExtra
              ) : showDownloadButton ? (
                <Button 
                  onClick={onDownload}
                  className="h-9 gap-2"
                >
                  Download
                  <BookDown className="h-4 w-4" />
                </Button>
              ) : showShareButton ? (
                <Button 
                  onClick={handleShare}
                  variant="ghost"
                  className="h-10"
                >
                  {t("common.share")}
                </Button>
              ) : (
                <button 
                  onClick={() => setIsInviteEarnOpen(true)}
                  className="inline-flex h-9 items-center justify-center rounded-full bg-primary px-3 text-center text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  {t("common.inviteFriends")}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between py-2 lg:hidden">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggleSidebar}
              className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-muted transition"
              aria-label="Toggle sidebar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M4 6h16" />
                <path d="M4 12h16" />
                <path d="M4 18h16" />
              </svg>
            </button>
            <a href="/dashboard">
              <img src={logo} className="h-10 w-auto cursor-pointer" alt="Shattara AI" />
            </a>
            
            {/* Mobile Content Title with Inline Edit */}
            {chat && (
              <div className="flex items-center gap-1.5 overflow-hidden">
                <span className="text-muted-foreground text-sm">/</span>
                {isEditingTitle ? (
                  <div className="flex items-center gap-1">
                    <Input
                      ref={inputRef}
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="h-7 w-28 text-xs"
                      placeholder="Title..."
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleSaveTitle}
                    >
                      <Check className="h-3.5 w-3.5 text-success" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleCancelEdit}
                    >
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={handleStartEdit}
                    className="flex items-center gap-1 text-xs font-medium text-foreground truncate max-w-[120px]"
                  >
                    <span className="truncate">{chat.title}</span>
                    <Pencil className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs font-medium">
            {isFreePlan && (
              <button 
                onClick={() => setIsPricingDialogOpen(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-success/40 bg-success/5 text-success hover:bg-success/10 transition-colors"
                aria-label="Upgrade"
              >
                <Crown className="h-4 w-4" />
              </button>
            )}
            {headerExtraMobile ? (
              headerExtraMobile
            ) : showDownloadButton ? (
              <Button 
                onClick={onDownload}
                size="icon"
                className="h-9 w-9"
                aria-label="Download report"
              >
                <BookDown className="h-4 w-4" />
              </Button>
            ) : showShareButton ? (
              <Button 
                onClick={handleShare}
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label={chat ? "Share content" : "Share room"}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            ) : (
              <button 
                onClick={() => setIsInviteEarnOpen(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                aria-label="Invite friends"
              >
                <Gift className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      <PricingDialog open={isPricingDialogOpen} onOpenChange={setIsPricingDialogOpen} />
      <InviteEarnDialog open={isInviteEarnOpen} onOpenChange={setIsInviteEarnOpen} />
    </header>
  );
};
